// components/LegalPage.js — Privacy, Cookies, Extension Privacy, Terms, Refunds, Legal Notice
// Global baseline: EU GDPR + ePrivacy, UK GDPR/PECR, US (CCPA/CPRA + state laws, GPC),
// Canada (PIPEDA, Quebec Law 25), Australia (Privacy Act/APPs, ACL), Singapore (PDPA),
// Brazil (LGPD), India (DPDP Act), with consumer-law carve-outs in Terms and Refunds.
//
// EDIT LEGAL_INFO BELOW. Empty strings are hidden on the page.
import React, { useEffect } from 'react';
import { Mail } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { SiteHeader, SiteFooter, SiteStyles } from './site/SiteChrome';
import { openCookieSettings } from '../utils/consent';

const LEGAL_INFO = {
  effectiveDate: 'November 2, 2026',
  company: 'TunaSignal AI, LLC',
  address: ['30 N Gould St Ste N', 'Sheridan, WY 82801', 'United States'],
  supportEmail: 'support@tunasignal.ai',
  privacyEmail: 'support@tunasignal.ai',        // TODO: privacy@tunasignal.ai if you create it
  privacyOfficer: 'the TunaSignal AI Privacy Officer', // role + contact only (Quebec Law 25 / Singapore PDPA need title and contact, not a personal name)
  phone: '',                                     // TODO: required in the French "mentions légales"
  companyRegistration: '2025-001755200',         // WY SOS Original ID, filed Aug 28, 2025
  // French LCEN: a publisher must name its publication director. Set this only if that
  // requirement applies to you; empty = the line is hidden on the page.
  publicationDirector: '',
  // Wyoming LLC with no EU/UK establishment => GDPR Art. 27 / UK GDPR Art. 27 representatives
  // are required (EU users are tracked/profiled, so the "occasional processing" exemption doesn't fit).
  euRepresentative: '',                          // TODO: name + address of EU representative
  ukRepresentative: '',                          // TODO: name + address of UK representative
  // Hosting provider (French LCEN 'mentions légales'). Empty = the Hosting block is hidden.
  // To restore: 'DigitalOcean, LLC, 105 Edgeview Drive, Suite 425, Broomfield, CO 80021, United States'
  host: '',
  retention: {
    accountMonthsAfterClosure: 1,
    securityLogsMonths: 12,
    analyticsMonths: 25,
    billingYears: 10,
  },
};

// ---------- small presentational helpers ----------
const H3 = ({ children }) => <h3 className="text-xl font-semibold mb-4 text-white">{children}</h3>;
const H4 = ({ children }) => <h4 className="text-base font-semibold mb-2 mt-4 text-white">{children}</h4>;
const P = ({ children, className = '' }) => <p className={`mb-6 leading-relaxed text-slate-300 ${className}`}>{children}</p>;
const UL = ({ children }) => <ul className="list-disc pl-6 space-y-2 mb-6 text-slate-300">{children}</ul>;
const Box = ({ title, tone = 'info', children }) => {
  const tones = {
    info: 'bg-[#07131F] border-[#28445F] text-white',
    warn: 'bg-[#F0414F]/10 border-[#F0414F]/30 text-[#FF7A85]',
    amber: 'bg-[#07131F] border-[#28445F] text-amber-300',
  };
  return (
    <div className={`p-4 rounded-lg border mb-8 ${tones[tone]}`}>
      <h4 className="font-semibold mb-3">{title}</h4>
      <div className="text-sm text-slate-300">{children}</div>
    </div>
  );
};
const Section = ({ id, title, children }) => (
  <section id={id} className="mb-12 scroll-mt-24">
    <div className="p-6 sm:p-8 rounded-md border bg-[#0D1E2E] border-[#1C3348]">
      <h2 className="ts-display-sub text-2xl sm:text-3xl text-white mb-6 pb-4 border-b border-[#1C3348]">{title}</h2>
      <div className="max-w-none">{children}</div>
    </div>
  </section>
);
const Table = ({ head, rows }) => (
  <div className="overflow-x-auto mb-6 rounded-lg border border-[#1C3348]">
    <table className="w-full text-sm text-left text-slate-300">
      <thead className="bg-[#07131F] text-slate-200">
        <tr>{head.map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-[#1C3348] align-top">
            {r.map((c, j) => <td key={j} className="px-3 py-2">{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
const Mail_ = ({ to }) => <a href={`mailto:${to}`} className="underline text-sky-300 hover:text-sky-200">{to}</a>;

const LegalPage = () => {
  useTheme(); // public pages use the terminal theme (matches LandingPage)
  const location = useLocation();
  const L = LEGAL_INFO;

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location.hash]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 80, behavior: 'smooth' });
  };

  const SECTIONS = [
    { id: 'privacy', label: 'Privacy Policy' },
    { id: 'cookies', label: 'Cookie Policy' },
    { id: 'webull-extension-privacy', label: 'Extension Privacy' },
    { id: 'terms', label: 'Terms & Conditions' },
    { id: 'refund', label: 'Refund & Cancellation' },
    { id: 'legal-notice', label: 'Legal Notice' },
  ];

  return (
    <div className="ts-page min-h-screen transition-colors bg-[#07131F]">
      <SiteStyles />
      <SiteHeader />

      <div className="pt-28 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="ts-display text-3xl sm:text-4xl text-white mb-2">Legal</h1>
          <p className="text-slate-400 mb-1">Privacy, cookies, terms and refunds for TunaSignal AI (Live Watch, Short Watch, Replay, ScalpWatch, Stock Watch, Alerts) and the Webull Sync extension.</p>
          <p className="text-slate-500 text-sm mb-6">Last updated: {L.effectiveDate}</p>
          <nav aria-label="Legal sections" className="flex flex-wrap gap-2 mb-10">
            {SECTIONS.map((sec) => (
              <button
                key={sec.id}
                onClick={() => scrollToSection(sec.id)}
                className="px-3 py-1.5 rounded-md border border-[#28445F] text-sm text-slate-300 hover:text-white hover:bg-[#13283C] transition-colors"
              >
                {sec.label}
              </button>
            ))}
            <button
              onClick={openCookieSettings}
              className="px-3 py-1.5 rounded-md border border-sky-400/40 text-sm text-sky-300 hover:text-white hover:bg-[#13283C] transition-colors"
            >
              Cookie settings
            </button>
          </nav>

          {/* ================= PRIVACY POLICY ================= */}
          <Section id="privacy" title="Privacy Policy">
            <Box title="Quick Summary">
              <ul className="space-y-1">
                <li>• We collect what we need to run TunaSignal AI, bill you, keep accounts secure and improve the service</li>
                <li>• We do not sell your personal information or share it for cross-context behavioral advertising</li>
                <li>• Card payments are handled by Stripe; we never see or store your full card number</li>
                <li>• Analytics run only after you allow them in our cookie banner</li>
                <li>• You can access, correct, export or delete your data by emailing <Mail_ to={L.privacyEmail} /></li>
              </ul>
            </Box>

            <H3>1. Who We Are</H3>
            <P>
              {L.company} ("TunaSignal AI", "we", "us"), a limited liability company registered in the State of Wyoming, USA (filing ID 2025-001755200),
              is the controller of the personal data described in this policy.
              Contact: <Mail_ to={L.privacyEmail} /> or {L.address.join(', ')}. Our person responsible for privacy
              (privacy officer / data protection contact) is {L.privacyOfficer}, reachable at the same email address.
              {L.euRepresentative && <> EU representative (GDPR Art. 27): {L.euRepresentative}.</>}
              {L.ukRepresentative && <> UK representative: {L.ukRepresentative}.</>}
            </P>

            <H3>2. Information We Collect</H3>
            <Table
              head={['Category', 'Examples', 'Source']}
              rows={[
                ['Account data', 'Name, username, email, password (hashed), Google sign-in profile (name, email) if you use it', 'You, Google'],
                ['Subscription & billing', 'Plan, trial status, trial-reward eligibility, discount codes, billing country, Stripe customer ID, invoices', 'You, Stripe'],
                ['Usage & activity', 'Pages viewed, features used, tickers looked up, alerts and filters you save, active-day records', 'Your use of the service'],
                ['Per-ticker alerts & requests', 'Tickers you switch Telegram alerts on for in Live Watch or Short Watch, the alert types and duration you choose, and tickers you ask us to re-analyse (Refresh)', 'You'],
                ['Device & security', 'IP address, browser/user-agent, device type, session tokens, login history', 'Your device'],
                ['Approximate location', 'Country/region/city derived from your IP address (never precise GPS)', 'IP lookup providers'],
                ['Integrations', 'Telegram chat ID if you connect Telegram; Webull sync settings (stored in your browser, see Extension Privacy)', 'You'],
                ['Communications', 'Support emails and messages, email preferences', 'You'],
                ['Anonymous visitors', 'Random visitor/session identifier, pages viewed, public score lookups, IP address', 'Your device (analytics only with consent)'],
              ]}
            />
            <P>
              We do not ask for and do not want sensitive data (health, religion, government IDs, etc.). We do not knowingly
              collect brokerage account credentials; the Webull extension uses your existing Webull session locally in your browser.
            </P>

            <H3>3. How We Use Your Information and Our Legal Bases</H3>
            <P>Where GDPR, UK GDPR, LGPD or similar laws apply, we rely on the following legal bases:</P>
            <Table
              head={['Purpose', 'Legal basis']}
              rows={[
                ['Create and run your account; deliver signals, alerts, per-ticker Telegram alerts, email notifications and on-demand re-analysis', 'Performance of a contract'],
                ['Rate limiting and queueing of on-demand requests (e.g. Refresh) to keep the service fair for everyone', 'Legitimate interests'],
                ['Billing, subscriptions, trials, trial rewards and discount codes', 'Performance of a contract; legal obligation (tax/accounting)'],
                ['Security, fraud and abuse prevention, single-device session enforcement', 'Legitimate interests (keeping the service and users safe)'],
                ['Product analytics for signed-in users (feature usage, errors, performance)', 'Legitimate interests (improving the service); you can object'],
                ['Analytics cookies and anonymous visitor tracking', 'Consent (cookie banner), withdrawable at any time'],
                ['Marketing emails and newsletters', 'Consent, or soft opt-in for existing customers where allowed; unsubscribe link in every email'],
                ['Responding to support requests and legal claims; complying with law', 'Legitimate interests; legal obligation'],
              ]}
            />
            <P>
              Trial-reward eligibility (extra trial days and discount codes) is calculated automatically from your daily activity.
              This does not produce legal or similarly significant effects; you can contact us to review any decision.
              Our AI scores, Short Watch stages, trade plans and summaries are about securities, not about you. Replay
              Practice results and your Short Watch filters and weights stay in your browser and are not sent to us.
            </P>

            <H3>4. Sharing Your Information</H3>
            <P>We do not sell or rent personal information. We share it only with:</P>
            <UL>
              <li><strong>Service providers (processors)</strong> acting on our instructions: our cloud hosting provider (USA), Stripe (payments), Google (Sign-In, email delivery, Google Analytics with consent, YouTube video embeds), Telegram (only if you connect it), ipinfo.io and ipapi.co (IP-to-location lookups).</li>
              <li><strong>Authorities</strong> when required by law or to protect rights, safety and property.</li>
              <li><strong>Business transfers</strong> in a merger, acquisition or sale of assets, under equivalent protections, with notice to you.</li>
            </UL>

            <H3>5. International Data Transfers</H3>
            <P>
              We are based in the United States and your data is processed there.
              For transfers from the EEA, UK and Switzerland we rely on the EU-U.S. Data Privacy Framework where the recipient is
              certified, and otherwise on the European Commission's Standard Contractual Clauses (with the UK Addendum), plus
              supplementary measures such as encryption in transit. For Canada, Australia, Singapore and other countries we use
              contractual safeguards that give comparable protection. You can request a copy of the relevant safeguards.
            </P>

            <H3>6. Data Security</H3>
            <P>
              We use encryption in transit (TLS), hashed passwords, access controls, rate limiting and session management to protect
              your data. No system is perfectly secure. If a breach is likely to affect your rights, we will notify you and the
              competent authorities as required by law (for example within 72 hours under GDPR).
            </P>

            <H3>7. Data Retention</H3>
            <UL>
              <li><strong>Account data:</strong> while your account is open, then deleted or anonymised within {L.retention.accountMonthsAfterClosure} month(s) of closure (backups roll off within 35 days).</li>
              <li><strong>Billing records:</strong> up to {L.retention.billingYears} years, as required by tax and accounting law.</li>
              <li><strong>Security and login logs:</strong> {L.retention.securityLogsMonths} months.</li>
              <li><strong>Analytics data:</strong> up to {L.retention.analyticsMonths} months; analytics cookies expire after 13 months at most.</li>
              <li><strong>Support messages:</strong> up to 3 years after the last exchange.</li>
            </UL>

            <H3>8. Your Rights</H3>
            <P className="mb-4">Depending on where you live, you may have the right to:</P>
            <UL>
              <li>Know and access the personal data we hold about you, and receive it in a portable format</li>
              <li>Correct inaccurate data and delete your data</li>
              <li>Restrict or object to processing, including processing based on legitimate interests and direct marketing</li>
              <li>Withdraw consent at any time (this does not affect earlier processing)</li>
              <li>Opt out of the sale or sharing of personal information and of targeted advertising (we do neither)</li>
              <li>Limit use of sensitive personal information (we do not collect it)</li>
              <li>Not be discriminated against for exercising your rights</li>
              <li>Appeal our decision on your request, and complain to a data protection authority</li>
            </UL>
            <P>
              To exercise a right, email <Mail_ to={L.privacyEmail} /> from your account email address. We may need to verify your
              identity. We respond within one month (GDPR/UK), 45 days (US state laws), 30 days (Canada, Australia, Singapore) or the
              period your local law sets, and may extend where the law allows. An authorised agent may submit a request for you with
              proof of authority. We honour the <strong>Global Privacy Control</strong> browser signal as an opt-out.
            </P>

            <H4>Regional information</H4>
            <UL>
              <li><strong>EU/EEA:</strong> you may complain to your local supervisory authority (in France, the CNIL).</li>
              <li><strong>United Kingdom:</strong> Information Commissioner's Office (ico.org.uk).</li>
              <li><strong>California and other US states:</strong> in the last 12 months we collected the categories in Section 2 (identifiers, customer records, commercial information, internet activity, approximate geolocation, inferences about product usage) for the purposes in Section 3. We have not sold or shared personal information, and have no actual knowledge of selling or sharing data of consumers under 16.</li>
              <li><strong>Canada:</strong> Office of the Privacy Commissioner of Canada; in Quebec, the Commission d'accès à l'information. Our privacy officer is listed in Section 1.</li>
              <li><strong>Australia:</strong> we handle personal information consistently with the Australian Privacy Principles; complaints may go to the OAIC (oaic.gov.au) if we do not resolve yours within 30 days.</li>
              <li><strong>Singapore:</strong> our data protection officer is listed in Section 1; you may also contact the PDPC.</li>
              <li><strong>Brazil:</strong> you have the rights in Art. 18 of the LGPD and may contact the ANPD.</li>
              <li><strong>India:</strong> our grievance contact is the privacy officer listed in Section 1; you may escalate to the Data Protection Board of India.</li>
            </UL>

            <H3>9. Cookies and Similar Technologies</H3>
            <P>See our <button onClick={() => scrollToSection('cookies')} className="underline text-sky-300">Cookie Policy</button> below.</P>

            <H3>10. Children</H3>
            <P>
              The service is only for adults 18 and over. We do not knowingly collect data from anyone under 18. If you believe a
              minor has given us data, contact us and we will delete it.
            </P>

            <H3>11. AI-Generated Content</H3>
            <P>
              Scores, signals, trade plans (entries, stops, targets), Short Watch stages and short scores, backtests, summaries of
              public filings and some audio narration on TunaSignal AI are generated or assisted by AI and automated models. They may be incomplete or wrong and are labelled as AI-generated where shown. Messages you send to
              support may be stored to answer you.
            </P>

            <H3>12. Changes to This Policy</H3>
            <P>
              We will post any update here with a new "Last updated" date. If a change is material, we will tell you by email or in
              the app before it takes effect, and ask for your consent again where the law requires it.
            </P>
          </Section>

          {/* ================= COOKIE POLICY ================= */}
          <Section id="cookies" title="Cookie Policy">
            <P>
              We use cookies and browser storage (localStorage/sessionStorage). Strictly necessary items are always on. Analytics
              run only if you accept them in the banner. Your choice is kept for 6 months, after which we ask again. Change it any
              time:{' '}
              <button onClick={openCookieSettings} className="underline text-sky-300 hover:text-sky-200">Cookie settings</button>.
            </P>
            <Table
              head={['Name', 'Provider', 'Purpose', 'Category', 'Duration']}
              rows={[
                ['access_token, refresh_token, currentUser, session_info, session_expires', 'TunaSignal AI', 'Keep you signed in and secure your session', 'Strictly necessary', 'Until logout / session expiry'],
                ['subscriptionInfo, subscriptionCheckedAt, rememberMe', 'TunaSignal AI', 'Remember subscription state and your "remember me" choice', 'Strictly necessary', 'Until logout'],
                ['tunasignal_cookie_consent', 'TunaSignal AI', 'Store your cookie choice', 'Strictly necessary', '6 months'],
                ['tunasignal_landing_tour_seen, tunasignal_landing_tour_voice, webull_ext_banner_dismissed, theme', 'TunaSignal AI', 'Remember UI preferences you set', 'Preferences (necessary for the feature you asked for)', 'Until you clear browser data'],
                ['tunasignal_livewatch_onboarding_*, tunasignal_shortwatch_onboarding_*', 'TunaSignal AI', 'Remember that you have seen the Live Watch / Short Watch tours and your narration choice', 'Preferences (necessary for the feature you asked for)', 'Until you clear browser data'],
                ['tsw_layout, tsw_tab, tsw_sort_v2, tsw_hw, tsw_feed_hidden, tsw_alert_defaults_v2', 'TunaSignal AI', 'Live Watch layout, tab, sort, hidden feed types and your per-ticker alert defaults', 'Preferences (necessary for the feature you asked for)', 'Until you clear browser data'],
                ['ssw_filters_v1, ssw_weights_v0, ssw_tab', 'TunaSignal AI', 'Short Watch filters, factor weights and tab', 'Preferences (necessary for the feature you asked for)', 'Until you clear browser data'],
                ['rp-practice-record', 'TunaSignal AI', 'Your Replay Practice score, kept only in your browser', 'Preferences (necessary for the feature you asked for)', 'Until you clear browser data'],
                ['Session-only keys (sessionStorage)', 'TunaSignal AI', 'Session messages and checkout state', 'Strictly necessary', 'Until the tab closes'],
                ['_ga, _ga_*', 'Google Analytics', 'Aggregate visitor statistics (no ads features)', 'Analytics (consent)', 'Up to 13 months'],
                ['Visitor/session identifiers', 'TunaSignal AI', 'Our own anonymous visitor statistics', 'Analytics (consent)', 'Up to 13 months'],
                ['YouTube player storage', 'Google (YouTube)', 'Plays embedded videos in privacy-enhanced mode; YouTube may store data when you press play', 'Embedded content (on play)', 'Set by YouTube'],
              ]}
            />
            <P>You can also block or delete cookies in your browser settings; some features may then stop working.</P>
          </Section>

          {/* ================= EXTENSION PRIVACY ================= */}
          <Section id="webull-extension-privacy" title="Browser Extension Privacy Policy">
            <p className="mb-6 text-sm text-slate-400">
              This section applies specifically to the <strong>TunaSignal &rarr; Webull Watchlist Sync</strong> browser
              extension. Effective date: July 16, 2026.
            </p>
            <Box title="Quick Summary">
              <ul className="space-y-1">
                <li>• The extension keeps your Webull watchlist in sync with your TunaSignalAI picks</li>
                <li>• It stores your sync settings and session tokens locally in your browser</li>
                <li>• It communicates only with TunaSignalAI and Webull — no one else</li>
                <li>• It does not collect your browsing history and does not sell or share your data</li>
                <li>• Your data is removed when you disconnect or uninstall the extension</li>
              </ul>
            </Box>

            <H3>1. Single Purpose</H3>
            <P>
              The extension has one purpose: to keep your Webull watchlist synchronized with the stock tickers that match the
              criteria you save on TunaSignalAI. It reads your filtered ticker list from your TunaSignalAI account and adds or
              removes those tickers in your own Webull watchlist.
            </P>

            <H3>2. Information the Extension Handles</H3>
            <UL>
              <li><strong>Authentication tokens:</strong> Your TunaSignalAI session token and your Webull session token, used only to access your own accounts on your behalf.</li>
              <li><strong>Sync settings:</strong> Your on/off preference and chosen sync interval.</li>
              <li><strong>Sync results:</strong> A record of the last sync (which tickers were added, removed, or kept) shown in the extension popup.</li>
            </UL>
            <P>
              All of this is stored locally in your browser using the browser&rsquo;s extension storage. The extension does not
              collect your name, email, browsing history, keystrokes, or any content of the pages you visit.
            </P>

            <H3>3. How the Information Is Used</H3>
            <UL>
              <li>The TunaSignalAI token is sent to TunaSignalAI to fetch your filtered ticker list.</li>
              <li>The Webull token is sent to Webull&rsquo;s own service to read and update your watchlist.</li>
              <li>Your settings and last-sync record are used to run and display the sync.</li>
            </UL>

            <H3>4. Permissions and Why They Are Needed</H3>
            <UL>
              <li><strong>storage:</strong> To save your sync settings and session tokens locally in the browser.</li>
              <li><strong>alarms:</strong> To run the sync on the schedule you select (for example, every 5 minutes).</li>
              <li><strong>webRequest:</strong> To read your own Webull session token from the requests the Webull web app already makes, so your watchlist can be updated. It only observes requests to Webull&rsquo;s domains and does not block or modify them.</li>
              <li><strong>Host access (tunasignal.ai, webull.com, webullfintech.com):</strong> These are the only sites the extension communicates with.</li>
            </UL>

            <H3>5. Data Sharing</H3>
            <P>
              We do not sell, rent, or share your data with third parties. The extension transmits data only to TunaSignalAI (our
              own service) and to Webull (to act on your own account). It does not send data to any advertising network, analytics
              provider, or other third party. Use of data received from browser APIs complies with the Chrome Web Store User Data
              Policy, including the Limited Use requirements.
            </P>

            <H3>6. Data Retention and Removal</H3>
            <P>
              Your settings and tokens remain in your browser&rsquo;s local storage until you disconnect within the extension or
              uninstall it, at which point they are removed. Session tokens naturally expire and are refreshed from your active
              sessions as needed.
            </P>

            <H3>7. Relationship to Webull</H3>
            <P>
              Webull is a trademark of its respective owner. This extension is an independent tool and is not affiliated with,
              endorsed by, or sponsored by Webull. It acts only on the Webull account you are already signed in to, at your direction.
              Your use of Webull remains subject to Webull&rsquo;s own terms.
            </P>

            <H3>8. Contact and Changes</H3>
            <P>
              Questions: <Mail_ to={L.privacyEmail} />. Material changes will be reflected here with an updated effective date.
              Your rights in the Privacy Policy above also apply.
            </P>
          </Section>

          {/* ================= TERMS ================= */}
          <Section id="terms" title="Terms & Conditions">
            <Box title="Important legal notice" tone="warn">
              <p>
                TunaSignal AI is an information and research tool, not an investment adviser or broker. Trading involves substantial
                risk of loss. These Terms contain limitations of liability and, for US users only, an arbitration agreement and class
                action waiver (Section 13). Nothing in these Terms removes rights you have under the mandatory consumer laws of the
                country where you live.
              </p>
            </Box>

            <H3>1. Acceptance and Eligibility</H3>
            <P>
              By creating an account or using TunaSignal AI you agree to these Terms and acknowledge our Privacy Policy. You must be
              at least 18 and legally able to enter a contract. You may not use the service where doing so is prohibited by local
              law, or if you are subject to sanctions.
            </P>

            <H3>2. Description of Service</H3>
            <P>
              We provide AI-generated scores, momentum signals, alerts, summaries of public filings and related tools based on market
              data and public information. Prices come from a live WebSocket market data feed; if that feed is unavailable, a backup source is used that may be delayed. Features may change as we improve the product (see Section 8). The service currently
              includes:
            </P>
            <UL>
              <li><strong>Live Watch:</strong> a live list of scored stocks with a ticker panel (activity, alerts, chart, dilution and squeeze details), a live alert feed and per-ticker Telegram alerts</li>
              <li><strong>Short Watch:</strong> a list of large same-day gainers checked for a possible fade, with a stage verdict, short score, borrow and squeeze-risk data, a hypothetical short plan and a historical backtest</li>
              <li><strong>Replay:</strong> bar-by-bar playback of past alerts, and a Practice mode with simulated trades on hidden past alerts</li>
              <li><strong>ScalpWatch, Stock Watch and Alerts:</strong> the scalping dashboard and the original list and alert pages</li>
              <li><strong>Integrations:</strong> Telegram and email alerts, and the Webull Watchlist Sync browser extension</li>
            </UL>

            <H3>3. No Investment Advice</H3>
            <P className="mb-4">
              All content is general and impersonal. It does not consider your objectives, financial situation or needs, is not a
              recommendation to buy or sell any security, and is not financial, investment, tax or legal advice. We are not registered
              as an investment adviser, broker-dealer or financial adviser with the SEC, FINRA, the FCA, ASIC, MAS, the Canadian
              Securities Administrators or any other regulator.
            </P>
            <P>
              <strong>Australia:</strong> any advice is general advice only. Consider whether it is appropriate for you and read the
              relevant disclosure documents before acting. <strong>UK:</strong> this content is not a financial promotion directed at
              you. Consult a licensed professional before making investment decisions.
            </P>

            <H3>4. Investment Risks</H3>
            <P>
              Small-cap and low-priced stocks are highly volatile and you may lose all of your capital. Past performance, including
              any win rates, pump-hit rates or examples we publish, does not indicate future results. Market data may be delayed or
              inaccurate. You alone are responsible for your trading decisions.
            </P>
            <H4>Short selling (Short Watch)</H4>
            <P>
              Short selling carries risks beyond buying a stock. Because a price can rise without limit, losses on a short position
              can exceed the amount you invested. Borrowed shares can be recalled, borrow fees can change during the day, a locate may
              not be available, short sale restrictions (SSR) and trading halts can block entries or exits, and low-float stocks can
              squeeze sharply higher. Borrow, fee and squeeze data shown on Short Watch may be delayed or incomplete; always confirm
              availability and costs with your broker. A stage or score is not an instruction to short.
            </P>
            <H4>Backtests, Replay and simulated results</H4>
            <P>
              Backtests, Replay playback, Practice mode results and any "pump hit" or win-rate figures are hypothetical. They are
              prepared with the benefit of hindsight, do not involve real money, may not reflect fees, slippage, liquidity or borrow
              availability at the time, and may use current data (such as today's borrow status) applied to past days. Simulated
              results have inherent limitations and do not mean you would have achieved similar results.
            </P>

            <H3>5. Your Account and Acceptable Use</H3>
            <UL>
              <li>Provide accurate information and keep your credentials confidential; one active session per account</li>
              <li>Use the service lawfully; do not use it for market manipulation, spam or other illegal activity</li>
              <li>Do not redistribute, resell, scrape or publish signals or content without written permission</li>
              <li>Do not reverse engineer, overload or interfere with the service or bypass access controls</li>
            </UL>

            <H3>6. Subscriptions, Automatic Renewal and Cancellation</H3>
            <P>
              Paid plans are billed in advance through Stripe and <strong>renew automatically</strong> at the end of each billing
              period at the price shown at checkout until you cancel. Before you pay we show the price, billing period, renewal terms
              and how to cancel. You can cancel online at any time from your Profile; cancellation stops the next renewal and you keep
              access until the end of the current paid period. Where required by law we send renewal reminders before annual
              renewals. We will give at least 30 days' notice of any price change, and you may cancel before it applies. Prices may
              include or exclude taxes depending on your location, as shown at checkout.
            </P>

            <H3>7. Free Trial and Introductory Discount</H3>
            <UL>
              <li>The free trial covers your first 5 US market days (Monday to Friday, excluding NYSE holidays, counted in US Eastern Time). If you sign up on a weekend or holiday, it starts on the next market day. No payment card is needed and the trial does not convert into a paid plan automatically.</li>
              <li>If you subscribe to the Monthly plan during your trial or within 72 hours after it ends, 50% is taken off your first 6 monthly payments ($100/month instead of $200/month). The discount is applied automatically at checkout.</li>
              <li>After the 6 discounted months, your subscription renews at the standard price ($200/month) until you cancel.</li>
              <li>One trial per person; we may refuse or end trials or discounts obtained through multiple accounts or abuse.</li>
              <li>If you started a trial under our earlier program, its original terms continue to apply to you. We may change the trial offer for new sign-ups with notice on our website; discounts already granted will be honoured.</li>
            </UL>

            <H3>8. Changes to the Service and to These Terms</H3>
            <P>
              We may update, add or remove features for legitimate reasons such as security, legal requirements or product
              improvements. If a change materially reduces what you paid for, we will notify you in advance and you may cancel and
              receive a pro-rata refund of prepaid fees for the unused period. We will notify you of material changes to these Terms at
              least 30 days before they apply; if you do not agree you may cancel before then.
            </P>

            <H3>9. Intellectual Property</H3>
            <P>
              The service, content and signals are owned by TunaSignal AI or its licensors. We grant you a personal, non-exclusive,
              non-transferable licence to use them for your own non-commercial purposes during your subscription.
            </P>

            <H3>10. Third-Party Services</H3>
            <P>
              The service links to or integrates with third parties such as Webull, Telegram, TradingView, YouTube and Stripe. Their
              terms and privacy policies apply to your use of them and we are not responsible for them.
            </P>

            <H3>11. Disclaimer of Warranties</H3>
            <P>
              To the maximum extent permitted by law, the service is provided "as is" and "as available". We do not guarantee
              accuracy, availability, profits or protection from losses. This does not affect statutory rights that cannot be
              excluded, such as the consumer guarantees under the Australian Consumer Law or the conformity rights of EU and UK
              consumers.
            </P>

            <H3>12. Limitation of Liability</H3>
            <P className="mb-4">
              To the maximum extent permitted by law, we are not liable for indirect, incidental, special, consequential or punitive
              damages, or for trading losses, lost profits or missed opportunities. Our total liability for any claim is limited to the
              greater of (a) the amount you paid us in the 12 months before the claim and (b) US$100.
            </P>
            <P>
              Nothing in these Terms limits liability for death or personal injury caused by negligence, for fraud or fraudulent
              misrepresentation, for gross negligence or wilful misconduct, or any other liability that cannot be limited under
              applicable law. If you are a consumer in the EU or UK, we are responsible for foreseeable loss caused by our breach of
              these Terms.
            </P>

            <H3>13. Governing Law and Disputes</H3>
            <P className="mb-4">
              These Terms are governed by the laws of the State of Wyoming, USA. If you are a consumer living outside the United States,
              you also keep the protection of the mandatory laws of your country of residence and may bring proceedings in your local
              courts. Please contact <Mail_ to={L.supportEmail} /> first; most issues can be solved informally.
            </P>
            <P className="mb-4">
              <strong>Arbitration (US residents only):</strong> any dispute will be resolved by binding individual arbitration
              administered by the American Arbitration Association under its Consumer Arbitration Rules, and you and we waive the right
              to a jury trial and to class or representative actions. Either party may bring an individual claim in small-claims court
              instead. You may opt out of this arbitration agreement by emailing <Mail_ to={L.supportEmail} /> within 30 days of
              first accepting these Terms.
            </P>
            <P>
              This arbitration agreement and class waiver do not apply to consumers in the EU/EEA, the UK, Quebec, Australia or any
              other place where such terms are not enforceable against consumers.
            </P>

            <H3>14. Suspension and Termination</H3>
            <P>
              You may close your account at any time. We may suspend or terminate accounts that materially breach these Terms, after
              notice where reasonable. If we terminate without cause, we will refund prepaid fees for the unused period.
            </P>

            <H3>15. Force Majeure</H3>
            <P>
              We are not liable for delays or failures caused by events beyond our reasonable control, including outages of data
              providers, cyberattacks, natural disasters or government action.
            </P>

            <H3>16. General</H3>
            <UL>
              <li><strong>Assignment:</strong> you may not assign these Terms; we may assign them in a business transfer, provided your rights are not reduced</li>
              <li><strong>Severability:</strong> if any provision is invalid, the rest remains in effect</li>
              <li><strong>Entire agreement:</strong> these Terms, the Privacy Policy and any offer terms shown at checkout are the whole agreement</li>
              <li><strong>Language:</strong> these Terms are written in English; if translated, the English version prevails to the extent permitted by law</li>
            </UL>
          </Section>

          {/* ================= REFUNDS ================= */}
          <Section id="refund" title="Refund & Cancellation Policy">
            <Box title="In short" tone="amber">
              <p>
                You can try TunaSignal AI free for 5 US market days with no card, cancel online at any time, and keep access until the end of
                the period you paid for. Outside the cases below, payments for periods already started are not refunded. This policy
                never limits your statutory consumer rights.
              </p>
            </Box>

            <H3>1. Cancelling</H3>
            <UL>
              <li>Cancel any time from your Profile, or by emailing <Mail_ to={L.supportEmail} /></li>
              <li>Cancellation takes effect at the end of your current billing period; you will not be charged again</li>
              <li>We do not offer pro-rata refunds for partly used periods, except as stated below or required by law</li>
            </UL>

            <H3>2. EU, EEA and UK Consumers: 14-Day Right of Withdrawal</H3>
            <P className="mb-4">
              You may withdraw from a paid subscription within 14 days of purchase without giving a reason by emailing{' '}
              <Mail_ to={L.supportEmail} /> with a clear statement. At checkout you ask us to start the service immediately; if you
              then withdraw within 14 days, we refund what you paid minus a proportionate amount for the days you used.
            </P>
            <P>We refund using the original payment method within 14 days of receiving your withdrawal.</P>

            <H3>3. Australia</H3>
            <P>
              Our services come with guarantees that cannot be excluded under the Australian Consumer Law. For a major failure you are
              entitled to cancel and receive a refund for the unused portion, or compensation for its reduced value. You may also be
              entitled to compensation for other reasonably foreseeable loss.
            </P>

            <H3>4. Other Refunds</H3>
            <UL>
              <li><strong>Billing errors and duplicate charges</strong> are always refunded</li>
              <li><strong>Prolonged unavailability</strong> of the service caused by us may be compensated or refunded pro rata</li>
              <li><strong>Material service reductions or termination without cause</strong>: pro-rata refund of the unused period (see Terms, Sections 8 and 14)</li>
              <li>Any money-back guarantee or promotion will state its own conditions</li>
            </UL>

            <H3>5. Trading Outcomes</H3>
            <P>
              Signals are informational only. Refunds are not given because of trading results, account performance or missed
              opportunities.
            </P>

            <H3>6. How to Request a Refund</H3>
            <UL>
              <li>Email <Mail_ to={L.supportEmail} /> from your account email with your invoice or the date of the charge</li>
              <li>We reply within 5 business days</li>
              <li>Approved refunds go back to the original payment method through Stripe; your bank may take 5–10 business days to show them</li>
            </UL>
          </Section>

          {/* ================= LEGAL NOTICE / CONTACT ================= */}
          <Section id="legal-notice" title="Legal Notice & Contact">
            <div className="grid md:grid-cols-2 gap-8 text-slate-300">
              <div>
                <h3 className="text-lg font-semibold mb-3 text-white">Publisher</h3>
                <p>
                  {L.company}<br />
                  {L.address.map((line) => <React.Fragment key={line}>{line}<br /></React.Fragment>)}
                  Wyoming close limited liability company, organized August 28, 2025
                  {L.companyRegistration && <> — Wyoming Secretary of State filing ID {L.companyRegistration}</>}<br />
                  {L.phone && <>Phone: {L.phone}<br /></>}
                  {L.publicationDirector && <>Publication director: {L.publicationDirector}</>}
                </p>
              </div>
              <div>
                {L.host && (
                  <>
                    <h3 className="text-lg font-semibold mb-3 text-white">Hosting</h3>
                    <p className="mb-6">{L.host}</p>
                  </>
                )}
                <h3 className="text-lg font-semibold mb-3 text-white">Email</h3>
                <a href={`mailto:${L.supportEmail}`} className="flex items-center space-x-2 hover:underline">
                  <Mail className="w-5 h-5" />
                  <span>{L.supportEmail}</span>
                </a>
                <p className="mt-2 text-sm text-slate-400">Response within 5 business days</p>
              </div>
            </div>
          </Section>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
};

export default LegalPage;
